import { Card } from 'components/Card';
import { CardRow } from 'components/CardRow';
import { ModalScreenProps, ModalScreens } from 'components/Navigation/types';
import { Page } from 'components/Page';
import { PageHeader } from 'components/PageHeader';
import { Txt } from 'components/Txt';
import { useConfirmUnsaved } from 'hooks/useConfirm';
import { useEsc } from 'hooks/useEsc';
import { theme } from 'lib/theme';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { NAVIGATE_MODAL_BACK } from 'store/actions';
import { doEngagePropertySettingsSave } from 'store/remote/engageActions';
import { selectEngagePropertiesItem } from 'store/remote/engageSelectors';
import {
  BlockManagerPropertyFeatureEnums,
  PropertyDoorRepairResponsibility,
} from 'types/DTOs/BlockManagerDTOs';

const REPAIR_RESPONSIBILITY_OPTIONS = [
  {
    value: PropertyDoorRepairResponsibility.leaseholder,
    label: 'Leaseholders are Responsible for Repairs',
    description:
      'Leaseholders are shown self-service repair details and can upload evidence of the completed repair.',
  },
  {
    value: PropertyDoorRepairResponsibility.freeholder,
    label: 'Freeholders are Responsible for Repairs',
    description:
      'Leaseholders are directed to the managing agent to arrange the door repair and cannot carry out the repair themselves.',
  },
];

export const EngagePropertySettingsModal: React.FC<
  ModalScreenProps<ModalScreens.EngagePropertySettings>
> = ({ navigation, route }) => {
  const dispatch = useDispatch();
  const confirmUnsaved = useConfirmUnsaved();

  const { propertyId, settings, pageKey } = route.params;
  const loadedBrandedCommunications = settings.brandedCommunications ?? false;
  const loadedAllowAssessmentSummaryCommunications =
    settings.allowAssessmentSummaryCommunications ?? false;
  const loadedValue = settings.flatEntranceDoorRepairResponsibility;

  // Get update permission from data store
  const property = useSelector((state) =>
    selectEngagePropertiesItem(state, pageKey, propertyId),
  );
  const propertyFeatures = property?.features ?? [];
  const hasUpdatePermission = propertyFeatures.includes(
    BlockManagerPropertyFeatureEnums.propertysettings,
  );

  const [brandedCommunications, setBrandedCommunications] = React.useState(
    loadedBrandedCommunications,
  );
  const [
    allowAssessmentSummaryCommunications,
    setAllowAssessmentSummaryCommunications,
  ] = React.useState(loadedAllowAssessmentSummaryCommunications);
  const [value, setValue] =
    React.useState<PropertyDoorRepairResponsibility>(loadedValue);

  const isDirty =
    brandedCommunications !== loadedBrandedCommunications ||
    allowAssessmentSummaryCommunications !==
      loadedAllowAssessmentSummaryCommunications ||
    value !== loadedValue;

  const handleSave = React.useCallback(() => {
    if (!value) {
      return;
    }
    dispatch(
      doEngagePropertySettingsSave({
        propertyId,
        body: {
          brandedCommunications,
          allowAssessmentSummaryCommunications,
          flatEntranceDoorRepairResponsibility: value,
        },
      }),
    );
  }, [
    allowAssessmentSummaryCommunications,
    brandedCommunications,
    dispatch,
    propertyId,
    value,
  ]);

  const handleClose = React.useCallback(() => {
    if (isDirty) {
      confirmUnsaved({
        onSave: handleSave,
        onDiscard: () =>
          dispatch(NAVIGATE_MODAL_BACK([ModalScreens.EngagePropertySettings])),
      });
      return;
    }
    dispatch(NAVIGATE_MODAL_BACK([ModalScreens.EngagePropertySettings]));
  }, [confirmUnsaved, dispatch, handleSave, isDirty]);

  React.useEffect(() => {
    navigation?.setOptions({ gestureEnabled: !isDirty });
  }, [navigation, isDirty]);

  useEsc(handleClose);

  return (
    <Page
      scrollView
      header={
        <PageHeader
          title="Property Settings"
          onPressCancelButton={handleClose}
          onPressSaveButton={hasUpdatePermission ? handleSave : undefined}
        />
      }
    >
      {!hasUpdatePermission && (
        <Card>
          <CardRow
            label="No Permissions"
            labelColor={theme.colors.red}
            labelAlignTop
            value="Your account does not have permission to change these settings."
          />
        </Card>
      )}
      <Card label="Messages">
        <CardRow
          label="Branded Messages"
          checkedValue={brandedCommunications}
          onPress={() => setBrandedCommunications((current) => !current)}
          disabled={!hasUpdatePermission}
        />
        <CardRow
          label="Send Assessment Summaries"
          checkedValue={allowAssessmentSummaryCommunications}
          onPress={() =>
            setAllowAssessmentSummaryCommunications((current) => !current)
          }
          disabled={!hasUpdatePermission}
        />
      </Card>
      <Card label="Flat Entrance Door Repairs">
        {REPAIR_RESPONSIBILITY_OPTIONS.map((option) => (
          <CardRow
            key={option.value}
            label={option.label}
            labelAlignTop
            radioValue={value === option.value}
            onPress={() => setValue(option.value)}
            disabled={!hasUpdatePermission}
          >
            <Txt size="xxsmall" style={{ color: theme.colors.fieldValueText }}>
              {option.description}
            </Txt>
          </CardRow>
        ))}
      </Card>
    </Page>
  );
};
